import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Modal from '@mui/material/Modal'
import Box from '@mui/material/Box'

type Command = { label: string; to: string; hint?: string; keywords?: string }

const COMMANDS: Command[] = [
  { label: 'Go to Today', to: '/today', hint: 'T', keywords: 'home dashboard' },
  { label: 'Go to Library', to: '/library', hint: 'L', keywords: 'books' },
  { label: 'Start a session', to: '/quiz', hint: 'S', keywords: 'review quiz study' },
  { label: 'Add a book', to: '/library/add', hint: 'A', keywords: 'import' },
  { label: 'Go to Settings', to: '/settings', hint: ',', keywords: 'account preferences' },
]

export default function CmdKPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [wasOpen, setWasOpen] = useState(open)

  // Clear the query when the palette closes, during render rather than in an
  // effect (react-hooks/set-state-in-effect).
  if (open !== wasOpen) {
    setWasOpen(open)
    if (!open) setQuery('')
  }

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return COMMANDS
    return COMMANDS.filter(
      (c) => c.label.toLowerCase().includes(q) || (c.keywords ?? '').includes(q),
    )
  }, [query])

  const run = (to: string) => {
    onClose()
    navigate(to)
  }

  return (
    <Modal open={open} onClose={onClose} aria-label="Command palette">
      <Box
        sx={{
          position: 'absolute',
          top: '15%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(580px, calc(100vw - 32px))',
          bgcolor: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-xl)',
          boxShadow: 'var(--shadow-pop)',
          overflow: 'hidden',
          outline: 'none',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, borderBottom: '1px solid var(--border)' }}>
          <SearchGlyph />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && matches[0]) run(matches[0].to)
            }}
            placeholder="Search or jump to…"
            aria-label="Search commands"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: 'var(--ink)',
              fontFamily: 'var(--font-sans)',
              fontSize: 16,
            }}
          />
          <kbd style={kbdStyle}>Esc</kbd>
        </Box>

        <Box sx={{ p: 1, maxHeight: 360, overflowY: 'auto' }}>
          <div style={eyebrowStyle}>Commands</div>
          {matches.map((c) => (
            <button key={c.to} onClick={() => run(c.to)} style={rowStyle} className="cmdk-row">
              <span>{c.label}</span>
              {c.hint && <kbd style={kbdStyle}>{c.hint}</kbd>}
            </button>
          ))}
          {query.trim() && (
            <>
              <div style={eyebrowStyle}>Actions</div>
              <button onClick={() => run('/quiz')} style={rowStyle} className="cmdk-row">
                <span>Start a session</span>
              </button>
            </>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            p: 1.5,
            borderTop: '1px solid var(--border)',
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: 'var(--ink-3)',
          }}
        >
          <span>↵ select</span>
          <span>esc close</span>
        </Box>
      </Box>
    </Modal>
  )
}

const eyebrowStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: 1.6,
  textTransform: 'uppercase',
  color: 'var(--ink-3)',
  padding: '8px 12px 4px',
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: '10px 12px',
  border: 'none',
  background: 'transparent',
  borderRadius: 10,
  color: 'var(--ink)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14.5,
  cursor: 'pointer',
  textAlign: 'left',
}

const kbdStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  padding: '2px 6px',
  borderRadius: 6,
  background: 'var(--surface-2)',
  color: 'var(--ink-3)',
  border: '1px solid var(--border)',
}

function SearchGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--ink-3)" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  )
}
